import {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  FocusEventHandler,
  ReactElement,
  ReactNode,
  Ref,
  forwardRef,
  isValidElement,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTrackingId, useMergeRefs } from "../../../hooks";
import {
  DataTrackingId,
  LayoutUtilProps,
  Size,
  MaxLengthCounterProps,
} from "../../../types";
import {
  useLayoutPropsUtil,
  useOptionallyControlledState,
} from "../../../internal/hooks";
import { Helper, HelperProps } from "../../../internal/components";
import { warnDeprecatedErrorUsage } from "../../FieldMessage";
import { Spinner } from "../../Spinner";
import styles from "../TextField.module.scss";
import cx from "classnames";
import { FieldLabel, FieldLabelProps } from "../../FieldLabel";
import {
  childrenToString,
  stripInlineMarkdown,
  warnOnce,
} from "../../../internal/functions";

export type TextFieldProps = Omit<
  ComponentPropsWithoutRef<"input">,
  "size" | "prefix"
> &
  LayoutUtilProps & {
    /**
     * Error state for the field. Pass `true` to indicate error styling without a message.
     * Pass a string, string[], or ReactElement (deprecated) for error messages.
     */
    error?: boolean | string | ReactElement | string[];
    // TODO(v4.0.0): make label required and type string
    /**
     * Label for the field.
     * Omitting `label` is deprecated — it will be required in v4.0.0. Use `hideLabel` to visually hide it.
     * Passing `ReactNode` is deprecated — use a plain string with inline markdown instead.
     */
    label?: FieldLabelProps["children"];
    prefix?: string | ReactElement;
    suffix?: string | ReactElement;
    size?: Extract<Size, "small" | "medium" | "large">;
    type?: "text" | "email" | "tel" | "url" | "password" | "number";
    description?: HelperProps["description"];
    /**
     * @deprecated No longer used. Error messages always use `aria-live="assertive"`.
     */
    errorAriaLive?: HelperProps["errorAriaLive"];
    hint?: HelperProps["hint"];
    /**
     * Warning message(s) to display. Supports a single string or an array of strings.
     */
    warning?: string | string[];
    loading?: boolean;
    disabled?: boolean;
    labelProps?: FieldLabelProps;
    moreInfo?: ReactNode;
    /**
     * When `true`, hides the visible label and moves it to `aria-label` on the input.
     * Requires `label` to be set — the label is always needed for accessibility.
     * @default false
     */
    hideLabel?: boolean;
  } & MaxLengthCounterProps &
  DataTrackingId;

export const TextField = forwardRef(
  (props: TextFieldProps, ref: Ref<HTMLInputElement>) => {
    const { aiMark, ...labelPropsWithoutAiMark } = props.labelProps || {};

    const data = {
      label: childrenToString(props.label),
      labelProps: labelPropsWithoutAiMark,
      prefix: childrenToString(props.prefix),
      hint: childrenToString(props.hint),
      description: childrenToString(props.description),
      size: props.size,
      type: props.type,
    };

    const trackingId = useTrackingId({
      name: "TextField",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      error,
      errorAriaLive: _errorAriaLive,
      defaultValue: defaultValueProp,
      value: valueProp,
      label,
      moreInfo,
      prefix,
      suffix,
      size,
      type = "text",
      required,
      onChange,
      onFocus,
      onBlur,
      id: idProp,
      description,
      hint,
      showCounter,
      maxLength,
      loading,
      disabled,
      labelProps,
      style,
      placeholder,
      warning,
      "aria-label": ariaLabelProp,
      hideLabel = false,
      ...rest
    } = componentProps;

    const inputRef = useRef<HTMLInputElement>(null);
    const [isTyping, setIsTyping] = useState(false);

    // Use the optionally controlled state for value
    const [value, setValue] = useOptionallyControlledState<typeof valueProp>({
      controlledValue: valueProp,
      defaultValue: defaultValueProp,
    });

    const length = useMemo(() => {
      if (
        typeof value === "string" ||
        typeof value === "number" ||
        Array.isArray(value)
      ) {
        return String(value).length;
      }
      return 0;
    }, [value]);

    const onChangeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
      setValue(e.target.value);
      onChange?.(e);
      if (!isTyping) {
        setIsTyping(true);
      }
    };

    const blurHandler: FocusEventHandler<HTMLInputElement> = (e) => {
      onBlur?.(e);
      if (isTyping) {
        setIsTyping(false);
      }
    };

    const labelUid = useId();
    const id = idProp ?? labelUid;

    const prefixUid = useId();
    const suffixUid = useId();

    const helperUid = useId();
    const moreInfoUid = useId();

    warnDeprecatedErrorUsage("TextField", error);

    // TODO(v4.0.0): remove these warnings once label is required string
    if (label === undefined) {
      warnOnce(
        "TextField: label will be required in v4.0.0. Use hideLabel to visually hide the label.",
      );
    } else if (typeof label !== "string") {
      warnOnce(
        "TextField: Passing ReactNode to label is deprecated. Use a plain string with inline markdown instead.",
      );
    }

    const errorMessages =
      typeof error === "boolean" || error === undefined ? undefined : error;

    const styleCombined = { ...style, ...layoutStyles };

    const hasHelperText =
      hint || showCounter || errorMessages || warning || description;

    const ariaDescribedBy =
      [hasHelperText && helperUid, moreInfo && moreInfoUid]
        .filter(Boolean)
        .join(" ") || undefined;

    return (
      <div
        className={cx(styles["textfield"], className)}
        data-anv="textfield"
        style={styleCombined}
      >
        {/* TODO(v4.0.0): simplify to !hideLabel once label is required */}
        {label && !hideLabel && (
          <FieldLabel
            moreInfo={moreInfo}
            moreInfoId={moreInfo ? moreInfoUid : undefined}
            required={required}
            htmlFor={id}
            {...labelProps}
          >
            {label}
          </FieldLabel>
        )}
        <div
          className={cx(styles["input-wrapper"], {
            [styles["small"]]: size === "small",
            [styles["large"]]: size === "large",
          })}
          role="presentation"
          onClick={(e) => {
            inputRef.current?.focus();
            if (e.target !== inputRef.current) {
              inputRef.current?.click();
            }
          }}
        >
          {prefix ? (
            <div
              aria-hidden
              className={styles["prefix"]}
              id={`prefix${prefixUid}`}
            >
              {prefix}
            </div>
          ) : null}
          <input
            id={id}
            className={cx(styles["input"], {
              [styles["error"]]: error,
            })}
            onChange={onChangeHandler}
            onFocus={onFocus}
            onBlur={blurHandler}
            ref={useMergeRefs([ref, inputRef])}
            required={required}
            maxLength={maxLength}
            placeholder={placeholder}
            type={type}
            disabled={disabled}
            value={value ?? ""}
            data-tracking-id={trackingId}
            {...rest}
            aria-label={
              // TODO(v4.0.0): remove typeof check — label will always be string
              hideLabel && typeof label === "string"
                ? stripInlineMarkdown(label)
                : ariaLabelProp || (label ? undefined : placeholder) // TODO(v4.0.0): this can all be removed once label is required. Aria-label should no longer be a separate prop passed through.
            }
            aria-describedby={ariaDescribedBy}
            aria-invalid={error ? !!error : undefined}
          />
          {loading ? (
            <div className={styles["loading-spinner-wrapper"]}>
              <Spinner size="small" />
            </div>
          ) : null}
          {suffix ? (
            <div
              className={styles["suffix"]}
              aria-hidden
              id={`suffix${suffixUid}`}
            >
              {suffix}
            </div>
          ) : null}
        </div>
        {hasHelperText ? (
          <Helper
            id={helperUid}
            hint={hint}
            maxLength={maxLength}
            inputLength={length}
            isTyping={isTyping}
            showCounter={showCounter}
            error={
              typeof errorMessages === "string" || Array.isArray(errorMessages)
                ? errorMessages
                : undefined
            }
            warning={warning}
            errorMessage={
              isValidElement(errorMessages) ? errorMessages : undefined
            }
            description={description}
          />
        ) : null}
      </div>
    );
  },
);
TextField.displayName = "InternalTextField";
